import React from "react";
import { AlertTriangle, Clipboard, Eye, EyeOff, RefreshCw } from "lucide-react";
import { Button, Card } from "@dawa/ui";

type Props = { children: React.ReactNode };
type State = { error?: Error; showDetails: boolean; copyFeedback: "idle" | "copied" | "failed" };

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { showDetails: false, copyFeedback: "idle" };
  private copyTimeout?: number;

  static getDerivedStateFromError(error: Error): State {
    return { error, showDetails: false, copyFeedback: "idle" };
  }

  componentDidCatch(error: Error, info: any) {
    console.error("UI Error:", error, info);
  }

  componentWillUnmount() {
    if (this.copyTimeout) window.clearTimeout(this.copyTimeout);
  }

  private toggleDetails = () => {
    this.setState((prev) => ({ showDetails: !prev.showDetails }));
  };

  private copyDetails = async (details: string) => {
    try {
      if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(details);
      } else {
        const didCopy = window.prompt("Copiez les détails de l’erreur :", details);
        if (didCopy === null) return;
      }
      this.setState({ copyFeedback: "copied" });
    } catch (err) {
      console.warn("Failed to copy error details", err);
      this.setState({ copyFeedback: "failed" });
    } finally {
      if (this.copyTimeout) window.clearTimeout(this.copyTimeout);
      this.copyTimeout = window.setTimeout(() => this.setState({ copyFeedback: "idle" }), 2500);
    }
  };

  render() {
    const { error, showDetails, copyFeedback } = this.state;
    if (!error) return this.props.children;

    const friendlyMessage = error.message || "Une erreur inattendue est survenue.";
    const details = error.stack || String(error);

    return (
      <div className="flex min-h-screen items-center justify-center bg-muted/20 px-4 py-10">
        <Card className="w-full max-w-xl space-y-6 p-6 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-danger/10 text-danger">
            <AlertTriangle className="h-6 w-6" aria-hidden="true" />
          </div>
          <div className="space-y-2">
            <h2 className="text-2xl font-semibold">Oups, quelque chose s’est mal passé</h2>
            <p className="text-sm text-muted">
              Nous avons rencontré un problème lors du chargement de la page. Vous pouvez recharger
              ou copier les détails pour les partager avec l’équipe support.
            </p>
          </div>
          <div className="rounded-md bg-muted/20 p-4 text-left">
            <p className="text-sm font-medium text-muted">Message :</p>
            <p className="break-words text-sm">{friendlyMessage}</p>
          </div>
          <div className="flex flex-wrap justify-center gap-2">
            <Button onClick={() => location.reload()} className="inline-flex items-center gap-2">
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
              Recharger
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={this.toggleDetails}
              className="inline-flex items-center gap-2"
            >
              {showDetails ? (
                <>
                  <EyeOff className="h-4 w-4" aria-hidden="true" />
                  Masquer les détails
                </>
              ) : (
                <>
                  <Eye className="h-4 w-4" aria-hidden="true" />
                  Voir les détails
                </>
              )}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => this.copyDetails(details)}
              className="inline-flex items-center gap-2"
            >
              <Clipboard className="h-4 w-4" aria-hidden="true" />
              Copier les détails
            </Button>
          </div>
          {copyFeedback !== "idle" && (
            <p className={`text-xs ${copyFeedback === "copied" ? "text-success" : "text-danger"}`}>
              {copyFeedback === "copied"
                ? "Détails copiés dans le presse-papier."
                : "Impossible de copier automatiquement les détails."}
            </p>
          )}
          {showDetails && (
            <pre className="max-h-72 overflow-auto rounded-md bg-muted/20 p-4 text-left text-xs text-muted">
              {details}
            </pre>
          )}
        </Card>
      </div>
    );
  }
}
